import Vue from "vue";
import SelectChevron from "./SelectChevron";

export default {
  name: "AppSelect",
  components: {
    SelectChevron
  },
  model: {
    prop: "value",
    event: "change"
  },
  props: {
    options: {
      type: Array,
      required: true
    },
    idKey: {
      type: String,
      default: "id"
    },
    labelKey: {
      type: String,
      default: "title"
    },
    valueKey: {
      type: String,
      default: "value"
    },
    placeholder: {
      type: String,
      default: ""
    },
    optionClasses: {
      type: [Object, Array]
    },
    optionClassPrefix: {
      type: String,
      default: ""
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    inputStyles: {
      type: Object,
      default: null
    },
    iconStyles: {
      type: Object,
      default: null
    },
    theme: {
      type: String,
      default: "main"
    },
    size: {
      type: String,
      default: ""
    },
    iconSize: {
      type: [String, Number],
      default: 12
    },
    //handle incorrect values
    incorrect: {
      type: Boolean,
      default: true
    },
    incorrectTitle: {
      type: String,
      default: "N/A value: "
    },
    maxListHeight: {
      type: [String, Number],
      default: 200
    },
    invalid: Boolean,
    value: {}
  },
  data() {
    return {
      opened: false,
      listAtBottom: true,
      filterValue: "",
      typeAheadPointer: -1
    };
  },
  computed: {
    classes() {
      return {
        [`app-select_${this.theme}`]: this.theme,
        opened: this.opened,
        disabled: this.disabled,
        invalid: this.invalid
      };
    },
    transformedOptions() {
      const arr = this.options.map(option => {
        if (typeof option === "object") return option;
        return {
          [this.idKey]: option,
          [this.labelKey]: option,
          [this.valueKey]: option
        };
      });
      return arr;
    },
    filteredOptions() {
      if (!this.filterValue) return this.transformedOptions;

      const re = new RegExp(this.filterValue, "i");
      return this.transformedOptions.filter(
        option => ~option[this.labelKey].toString().search(re)
      );
    },
    dropdownStyles() {
      if (this.listAtBottom) {
        return {
          bottom: "0",
          transform: "translateY(100%)"
        };
      } else {
        return {
          bottom: "100%"
        };
      }
    }
  },
  watch: {
    filteredOptions() {
      this.typeAheadPointer = -1;
    }
  },
  methods: {
    getScrollElement() {
      return this.$refs.scrollArea.scrollElement;
    },
    checkSelection(option) {
      const val = option[this.valueKey];
      if (this.multiple) {
        return this.value.includes(val);
      } else {
        return this.value === val;
      }
    },
    selectOption(option) {
      if (this.multiple) {
        this.$emit("change", this.value.concat(option[this.valueKey]));
      } else {
        this.$emit("change", option[this.valueKey]);
      }
    },
    unselectOption(optionValue) {
      if (this.multiple) {
        const filteredArray = this.value.filter(item => item !== optionValue);
        this.$emit("change", filteredArray);
      } else if (this.allowEmpty) {
        this.$emit("change", null);
      }
    },
    getValueTitle(value) {
      if (value == null) return "";
      const item = this.transformedOptions.find(
        item => item[this.valueKey] === value
      );
      if (!item && !this.incorrect) {
        throw new Error("Selected value has not been found");
      }
      return (item && item[this.labelKey]) || this.incorrectTitle + value;
    },
    handleSelect(option) {
      if (this.checkSelection(option)) {
        this.unselectOption(option[this.valueKey]);
      } else {
        this.selectOption(option);
      }
      if (!this.multiple) this.$refs.input.blur();
    },
    openList() {
      this.opened = true;
      const notMultipleAndValue = !this.multiple && this.value;

      if (notMultipleAndValue) {
        this.typeAheadPointer = this.filteredOptions.findIndex(
          item => item[this.valueKey] === this.value
        );
      }
      // scroll to selected option
      Vue.nextTick(() => {
        if (!this.opened) return;
        const coords = this.$refs.selectContainer.getBoundingClientRect();
        const documentHeight = document.documentElement.clientHeight;
        const dropdown = this.$refs.dropdown;

        if (notMultipleAndValue) {
          const scrollElement = this.getScrollElement();
          const element = scrollElement.querySelector(
            ".app-select__item_selected"
          );

          if (!element) return;

          const optionHalfHeight = 18;

          scrollElement.scrollTop =
            element.offsetTop - (this.maxListHeight / 2 - optionHalfHeight);
        }

        if (coords.bottom + dropdown.offsetHeight > documentHeight) {
          this.listAtBottom = false;
        } else {
          this.listAtBottom = true;
        }
      });
    },
    closeList() {
      this.opened = false;
    },
    handleFocus() {
      this.openList();
    },
    handleBlur() {
      this.closeList();
    },
    handleClick() {
      if (this.disabled) return;
      if (this.opened) {
        this.$refs.input.blur();
      } else {
        this.$refs.input.focus();
      }
    },
    changeFilterValue(value) {
      value = value.trim();
      this.filterValue = value;
    },
    typeAheadUp() {
      if (this.typeAheadPointer > 0) {
        this.typeAheadPointer--;
        this.adjustScroll();
      }
    },
    typeAheadDown() {
      if (this.typeAheadPointer < this.filteredOptions.length - 1) {
        this.typeAheadPointer++;
        this.adjustScroll();
      }
    },
    typeAheadSelect() {
      if (this.filteredOptions[this.typeAheadPointer]) {
        this.handleSelect(this.filteredOptions[this.typeAheadPointer]);
        this.filterValue = "";
      }
    },
    onEscape() {
      this.$refs.input.blur();
      this.filterValue = "";
    },
    adjustScroll() {
      const pixelsToPointerTop = this.pixelsToPointerTop();
      const pixelsToPointerBottom = this.pixelsToPointerBottom();
      const scrollElement = this.getScrollElement();

      if (pixelsToPointerTop <= this.viewport().top) {
        scrollElement.scrollTop = pixelsToPointerTop;
      } else if (pixelsToPointerBottom >= this.viewport().bottom) {
        scrollElement.scrollTop = this.viewport().top + this.pointerHeight();
      }
    },
    viewport() {
      const scrollElement = this.getScrollElement();
      return {
        top: scrollElement.scrollTop,
        bottom: scrollElement.offsetHeight + scrollElement.scrollTop
      };
    },
    pixelsToPointerTop() {
      let pixelsToPointerTop = 0;
      for (let i = 0; i < this.typeAheadPointer; i++) {
        pixelsToPointerTop += this.$refs.options.children[i].offsetHeight;
      }
      return pixelsToPointerTop;
    },
    pixelsToPointerBottom() {
      return this.pixelsToPointerTop() + this.pointerHeight();
    },
    pointerHeight() {
      const element = this.$refs.options.children[this.typeAheadPointer];
      return element ? element.offsetHeight : 0;
    }
  }
};
